import { Button } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import {
  IBestDataset,
  IPlotVariable,
  RosePlotChart,
} from '../../../../../../types/analytics';
import { IGetRosePlotV2ApiPayload } from '../../../../../../types/chart';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { useRosePlotParams } from '../../../hooks/useRosePlotParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import { RosePlotAdditionalControls } from './RosePlotAdditionalInput';
import { RosePlotChart as RosePlotChartComponent } from './RosePlotChart';
import { RosePlotInputForm } from './RosePlotInputForm';

interface RosePlotProps {
  onDatasetChange: (item: string) => void;
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  datasets: IBestDataset[];
  defaultDirectionShortName?: string;
  defaultValueShortName?: string;
  variables: IPlotVariable[];
  dateRange: string[];
  plotData?: RosePlotChart;
  plotDefaultInterval: number;
  updatedRosePlotData: RosePlotChart | null;
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  errorUpdate: string;
  previewError: string;
  updating: boolean;
  onUpdateRosePlot: (values: IGetRosePlotV2ApiPayload) => void;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  onChangeActiveKey: (item: any) => void;
  pointOption: { label: string; value: string }[];
  setUpdatedRosePlotData: (item: any) => void;
  activePlot?: string;
  isOffline: boolean;
  enrichedMetadata: IEnrichedMetadata[];
  coordinates: number[];
}

export default function RosePlot(props: RosePlotProps) {
  const { errorUpdate, updating, pointName, previewError, isOffline } = props;

  const {
    rosePlotData,
    rosePlotSeries,
    variable,
    direction,
    directionalDataset,
    directionalDatasetIsOffline,
    doDatasetDirectionalChange,
    variablesX,
    directions,
    legendInterval,
    directionNo,
    directionChartNo,
    startDate,
    endDate,
    unitX,
    downloadName,
    degreeIntervals,
    categories,
    memoCoords,
    selectedDatasetInfo,
    doStartDateChange,
    doEndDateChange,
    doChangeDirectionNo,
    doChangeIntervalNo,
    handleCheckboxChange,
    doVariableChange,
    doDirectionChange,
    onUpdateRosePlot,
    disabledStartDate,
    disabledEndDate,
    month,
    doMonthChange,
  } = useRosePlotParams(props);

  const isOfflineDataset = isOffline || directionalDatasetIsOffline;

  // TODO: enable cache
  // const upsertRoseCache = useAnalyticsCacheStore(state => state.upsertRoseCache)

  // useEffect(() => {
  //   if (!props.coordinates || !props.previewDatasetId || !variable?.Index) return;

  //   const key = `${props.previewDatasetId}_${variable?.Index}_${props.coordinates[1]},${props.coordinates[0]}`;
  //   upsertRoseCache(key, {
  //     variableXIndex: variable?.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     rosePlotSeries,
  //     rosePlotData,
  //     directionChartNo,
  //     unitX,
  //     downloadName: downloadName ?? '',
  //     selectedDatasetInfo
  //   });
  // }, [upsertRoseCache, props.coordinates, props.previewDatasetId, variable?.Index, startDate, endDate, rosePlotData, rosePlotSeries, directionChartNo, unitX, downloadName, selectedDatasetInfo]);

  const plotInputContent = useMemo(
    () => (
      <RosePlotInputForm
        datasets={props.datasets}
        previewDatasetId={props.previewDatasetId}
        onDatasetChange={props.onDatasetChange}
        categories={categories}
        variables={variablesX}
        variable={variable}
        onVariableChange={doVariableChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={updating}
        hasError={!!previewError}
      />
    ),
    [
      props.datasets,
      props.previewDatasetId,
      props.onDatasetChange,
      categories,
      variablesX,
      variable,
      doVariableChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      updating,
      previewError,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          id='Analytics-RosePlot-Update_Button'
          disabled={!!previewError || isOfflineDataset}
          loading={updating}
          onClick={() => onUpdateRosePlot()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [previewError, updating, onUpdateRosePlot, isOfflineDataset]
  );

  const emptyText = useMemo(() => {
    if (props.previewDatasetId.includes('CMEMS')) {
      return 'Layer not available';
    }
  }, [props.previewDatasetId]);

  const chartContent = useMemo(
    () => (
      <RosePlotChartComponent
        rosePlotData={rosePlotData}
        rosePlotSeries={rosePlotSeries}
        pointName={pointName}
        coordinates={memoCoords}
        directionChartNo={directionChartNo}
        unitX={unitX}
        downloadName={downloadName ?? ''}
        selectedDatasetInfo={selectedDatasetInfo}
        isLoading={updating}
        error={errorUpdate || previewError}
        isOffline={isOfflineDataset}
        emptyText={emptyText}
      />
    ),
    [
      rosePlotData,
      rosePlotSeries,
      pointName,
      memoCoords,
      directionChartNo,
      unitX,
      downloadName,
      selectedDatasetInfo,
      updating,
      errorUpdate,
      previewError,
      isOfflineDataset,
      emptyText,
    ]
  );

  const additionalContent = useMemo(
    () => (
      <RosePlotAdditionalControls
        datasets={props.datasets}
        categories={categories}
        directions={directions}
        direction={direction}
        onDirectionChange={doDirectionChange}
        directionNo={directionNo}
        degreeIntervals={degreeIntervals}
        onChangeDirectionNo={doChangeDirectionNo}
        legendInterval={legendInterval}
        onChangeIntervalNo={doChangeIntervalNo}
        isLoading={updating}
        hasError={!!previewError}
        directionalDataset={directionalDataset}
        onDirectionalDatasetChange={doDatasetDirectionalChange}
        month={month}
        onMonthChange={doMonthChange}
      />
    ),
    [
      directions,
      direction,
      doDirectionChange,
      directionNo,
      degreeIntervals,
      doChangeDirectionNo,
      legendInterval,
      doChangeIntervalNo,
      updating,
      previewError,
      props.datasets,
      categories,
      month,
      doMonthChange,
      directionalDataset,
      doDatasetDirectionalChange,
    ]
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  );
}
